import React, { useState, useEffect } from 'react';
import { 
  X, 
  Send, 
  Download, 
  CreditCard, 
  Ban, 
  Edit, 
  Calendar,
  DollarSign,
  User,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle
} from 'lucide-react';
import { api } from '../../auth/authStore';

interface InvoiceLineItem {
  _id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  amount: number;
  taxAmount: number;
  total: number;
}

interface Payment {
  method: string;
  transactionId?: string;
  paidAt: Date;
  amount: number;
  notes?: string;
  processedBy?: {
    firstName: string;
    lastName: string;
  };
}

interface Invoice {
  _id: string;
  number: string;
  clientId: {
    _id: string;
    displayName: string;
    legalName: string;
    email: string;
    phone: string;
    address: {
      street: string;
      city: string;
      province: string;
      postalCode: string;
      country: string;
    };
  };
  issueDate: string;
  dueDate: string;
  currency: string;
  items: InvoiceLineItem[];
  totals: {
    subtotal: number;
    taxTotal: number;
    total: number;
    amountPaid: number;
    amountDue: number;
  };
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  payments: Payment[];
  notes?: string;
  terms?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: {
    firstName: string;
    lastName: string;
  };
}

interface InvoiceDetailsModalProps {
  isOpen: boolean;
  invoiceId: string | null;
  onClose: () => void;
  onUpdate: () => void;
}

export const InvoiceDetailsModal: React.FC<InvoiceDetailsModalProps> = ({
  isOpen,
  invoiceId,
  onClose,
  onUpdate,
}) => {
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && invoiceId) {
      fetchInvoice();
    }
  }, [isOpen, invoiceId]);

  const fetchInvoice = async () => {
    if (!invoiceId) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/firm-admin/billing/invoices/${invoiceId}`);
      setInvoice(response.data.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to fetch invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvoice = async () => {
    if (!invoice) return;
    
    setActionLoading('send');
    try {
      await api.post(`/firm-admin/billing/invoices/${invoice._id}/send`, {
        email: invoice.clientId.contact?.email,
        subject: `Invoice ${invoice.number}`,
        message: 'Please find your invoice attached.',
      });
      await fetchInvoice();
      onUpdate();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to send invoice');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelInvoice = async () => {
    if (!invoice) return;
    
    const reason = prompt('Please provide a reason for cancelling this invoice:');
    if (!reason) return;

    setActionLoading('cancel');
    try {
      await api.post(`/firm-admin/billing/invoices/${invoice._id}/cancel`, {
        reason,
      });
      await fetchInvoice();
      onUpdate();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to cancel invoice');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadPDF = async () => {
    if (!invoice) return;
    
    setActionLoading('download');
    try {
      const response = await api.get(`/firm-admin/billing/invoices/${invoice._id}/pdf`, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to download PDF');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft':
        return <FileText className="w-5 h-5 text-gray-500" />;
      case 'sent':
        return <Send className="w-5 h-5 text-blue-500" />;
      case 'paid':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'overdue':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-gray-500" />;
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number | undefined | null, currency: string = 'CAD') => {
    // Handle undefined, null, or NaN values
    const safeAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;

    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency,
    }).format(safeAmount);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900">
              {invoice ? `Invoice ${invoice.number}` : 'Invoice Details'}
            </h2>
            {invoice && (
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                {getStatusIcon(invoice.status)}
                <span className="ml-1 capitalize">{invoice.status}</span>
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          ) : invoice ? (
            <div className="p-6 space-y-6">
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                {invoice.status === 'draft' && (
                  <button
                    onClick={handleSendInvoice}
                    disabled={actionLoading === 'send'}
                    className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {actionLoading === 'send' ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Invoice
                  </button>
                )}

                {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                  <button
                    onClick={handleSendInvoice}
                    disabled={actionLoading === 'send'}
                    className="flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {actionLoading === 'send' ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Resend Invoice
                  </button>
                )}

                <button
                  onClick={handleDownloadPDF}
                  disabled={actionLoading === 'download'}
                  className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  {actionLoading === 'download' ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Download PDF
                </button>

                {(invoice.status === 'draft' || invoice.status === 'sent') && (
                  <button
                    onClick={handleCancelInvoice}
                    disabled={actionLoading === 'cancel'}
                    className="flex items-center px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-300 rounded-md hover:bg-red-100 disabled:opacity-50"
                  >
                    {actionLoading === 'cancel' ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                    ) : (
                      <Ban className="w-4 h-4 mr-2" />
                    )}
                    Cancel Invoice
                  </button>
                )}
              </div>

              {/* Invoice Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Client Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                    <User className="w-5 h-5 mr-2" />
                    Client Information
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Name:</span>
                      <span className="ml-2 text-gray-900">{invoice.clientId.displayName}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Legal Name:</span>
                      <span className="ml-2 text-gray-900">{invoice.clientId.legalName}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Email:</span>
                      <span className="ml-2 text-gray-900">{invoice.clientId.contact?.email}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Phone:</span>
                      <span className="ml-2 text-gray-900">{invoice.clientId.contact?.phone}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Address:</span>
                      <div className="ml-2 text-gray-900">
                        {invoice.clientId.address.street}<br />
                        {invoice.clientId.address.city}, {invoice.clientId.address.province} {invoice.clientId.address.postalCode}<br />
                        {invoice.clientId.address.country}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Invoice Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Invoice Details
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Issue Date:</span>
                      <span className="ml-2 text-gray-900">{formatDate(invoice.issueDate)}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Due Date:</span>
                      <span className="ml-2 text-gray-900">{formatDate(invoice.dueDate)}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Currency:</span>
                      <span className="ml-2 text-gray-900">{invoice.currency}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Created By:</span>
                      <span className="ml-2 text-gray-900">
                        {invoice.createdBy.firstName} {invoice.createdBy.lastName}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Created:</span>
                      <span className="ml-2 text-gray-900">{formatDate(invoice.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Line Items
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Unit Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tax Rate
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tax
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {invoice.items.map((item) => (
                        <tr key={item._id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.description}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.quantity}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(item.unitPrice, invoice.currency)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {((item.taxRate || 0) * 100).toFixed(1)}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(item.amount, invoice.currency)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(item.taxAmount, invoice.currency)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatCurrency(item.total, invoice.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="mt-4 flex justify-end">
                  <div className="w-64 bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">{formatCurrency(invoice.totals.subtotal, invoice.currency)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Tax Total:</span>
                      <span className="font-medium">{formatCurrency(invoice.totals.taxTotal, invoice.currency)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-semibold border-t border-gray-300 pt-2">
                      <span>Total:</span>
                      <span>{formatCurrency(invoice.totals.total, invoice.currency)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Amount Paid:</span>
                      <span className="font-medium">{formatCurrency(invoice.totals.amountPaid, invoice.currency)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-red-600">
                      <span>Amount Due:</span>
                      <span className="font-medium">{formatCurrency(invoice.totals.amountDue, invoice.currency)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment History */}
              {invoice.payments && invoice.payments.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <CreditCard className="w-5 h-5 mr-2" />
                    Payment History
                  </h3>
                  <div className="bg-gray-50 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Method
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Transaction ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Processed By
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {invoice.payments.map((payment, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(payment.paidAt.toString())}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <span className="capitalize">{payment.method}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                              {formatCurrency(payment.amount, invoice.currency)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {payment.transactionId || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {payment.processedBy
                                ? `${payment.processedBy.firstName} ${payment.processedBy.lastName}`
                                : '-'
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Notes and Terms */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {invoice.notes && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Notes</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{invoice.notes}</p>
                    </div>
                  </div>
                )}

                {invoice.terms && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Payment Terms</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{invoice.terms}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
